import { type FC, type ReactNode, useMemo, useSyncExternalStore } from 'react';

import { Provider } from 'urql';

import {
  getUrqlCacheVersion,
  subscribeToUrqlCacheVersion,
} from '@/src/features/integrations/urql/cache-version';
import { UrqlGraphqlClient } from '@/src/integrations/urql/client';

/**
 * Provides the Urql GraphQL client to the component tree.
 *
 * The client is derived from an external cache-version counter via {@link useSyncExternalStore}
 * rather than stored in React state. This has three benefits over a `useState` + context approach:
 *
 * 1. **Caller Freedom**: `invalidateUrqlCache` is a plain module function, callable from
 *    anywhere outside the context tree.
 * 2. **Tearing Prevention**: `useSyncExternalStore` is the React 18 API for subscribing to
 *    external mutable stores. It guarantees all components in a concurrent render pass see the
 *    same snapshot, preventing a torn tree where some components use the old client and others
 *    use the new one.
 * 3. **Derived State**: The client is a pure function of the version via `useMemo`, making the
 *    relationship explicit: when the version increments, a new client is produced automatically.
 */
export const UrqlGraphQlProvider: FC<{ readonly children: ReactNode }> = ({ children }) => {
  const version = useSyncExternalStore(
    subscribeToUrqlCacheVersion,
    getUrqlCacheVersion,
    getUrqlCacheVersion,
  );
  /* eslint-disable-next-line @eslint-react/exhaustive-deps */
  const client = useMemo(() => new UrqlGraphqlClient({}), [version]);
  return <Provider value={client}>{children}</Provider>;
};
